import { createContext, ReactNode, useContext, useEffect, useMemo } from "react";
import { languageManager } from "@/lib/language-manager";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { User as SelectUser } from "@shared/schema";

// Typ pre registráciu - zodpovedá backend insertUserSchema
type RegisterData = {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  market?: "SK" | "US";
  language?: string;
  currency?: string;
};
import { getQueryFn, apiRequest, queryClient } from "../lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useLocation } from "wouter";

type AuthContextType = {
  user: SelectUser | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<SelectUser, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<SelectUser, Error, RegisterData>;
};

type LoginData = {
  email: string;
  password: string;
  rememberMe?: boolean;
};

export const AuthContext = createContext<AuthContextType | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const { pathname } = useLocation();
  
  const currentPath = useMemo(() => pathname || window.location.pathname, [pathname]);
  const isAuthRoute = useMemo(() => currentPath.startsWith("/auth"), [currentPath]);
  
  console.log('[AUTH] Pathname check:', { pathname, currentPath, isAuthRoute });
  
  const {
    data: user,
    error,
    isLoading,
  } = useQuery<SelectUser | undefined, Error>({
    queryKey: ["/api/user"],
    queryFn: getQueryFn({ on401: "throw" }), // Use default behavior to throw on 401
    enabled: true, // Vždy spúšťaj user query
    onSuccess: (data) => {
      console.log('[AUTH] User query success:', data?.email, data?.role);
    },
    onError: (err) => {
      console.log('[AUTH] User query error:', err);
      // Ak dostaneme 401 a nie sme na auth stránke, redirect na auth
      if (err?.message?.includes('401') && !isAuthRoute) {
        console.log('[AUTH] 401 error detected, redirecting to auth');
        window.location.href = '/auth';
      }
    }
  });

  // Debug log pre React Query stav
  console.log('[AUTH] React Query state:', { user: user?.email, isLoading, error: error?.message });

  // Redirect na login ak nie je user a nie sme na auth stránke (fallback for non-401 cases)
  useEffect(() => {
    console.log('[AUTH] useEffect check:', { 
      hasError: !!error, 
      errorMessage: error?.message, 
      hasUser: !!user, 
      isLoading, 
      isAuthRoute, 
      currentPath
    });
    
    if (!user && !isLoading && !isAuthRoute) {
      console.log('[AUTH] No user detected in useEffect, redirecting to auth');
      window.location.href = '/auth';
    }
  }, [user, isLoading, isAuthRoute]);

  // Set language based on user locale
  useEffect(() => {
    if (user?.locale) {
      // Set language based on user's database locale
      languageManager.setLanguageFromUser(user.locale);
    } else if (!user && !isLoading) {
      // If no user is logged in, try to detect language from browser/localStorage
      const storedLang = localStorage.getItem('i18nextLng');
      const browserLang = navigator.language?.startsWith('sk') ? 'sk' : 'en';
      const defaultLang = storedLang || browserLang || 'sk';
      
      languageManager.setLanguage(defaultLang as 'sk' | 'en');
    }
  }, [user, isLoading]);

  // (retry policy for queries is configured per-query where relevant)

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      const res = await fetch("/api/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const errorMessage = typeof data?.message === "string" ? data.message : "Prihlásenie zlyhalo";
        const error: any = new Error(errorMessage);
        error.status = res.status;
        if (data?.requiresEmailVerification) {
          error.requiresEmailVerification = true;
          error.email = data?.email || credentials.email;
        }
        throw error;
      }
      return data;
    },
    onSuccess: (user: SelectUser) => {
      // Nastavíme nové údaje o používateľovi
      queryClient.setQueryData(["/api/user"], user);
      
      // Invalidujeme user query aby sa načítal nový stav
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      
      // Aktívne načítame nové údaje z endpointov, ktoré používateľ bude potrebovať
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/price-quotes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/system-settings/public"] });
      
      // Pre admin používateľov môžeme doinvalidovať aj ďalšie endpointy
      if (user.role === 'admin' || user.role === 'superadmin' || user.role === 'owner') {
        queryClient.invalidateQueries({ queryKey: ["/api/admin"] });
      }
    },
    onError: (error: Error) => {
      const typedError = error as Error & { requiresEmailVerification?: boolean; email?: string };
      if (typedError.requiresEmailVerification) {
        const email = typedError.email;
        toast({
          title: "Overte email",
          description: "Účet je vytvorený, ale email ešte nie je overený.",
          action: (
            <ToastAction
              altText="Preposlať verifikačný email"
              onClick={async () => {
                try {
                  const response = await apiRequest("POST", "/api/resend-verification", { email });
                  const data = await response.json().catch(() => null);
                  const success = data?.success !== false;
                  toast({
                    title: success ? "Verifikačný email odoslaný" : "Email neodoslaný",
                    description: data?.message || "Skontrolujte svoj email a kliknite na overovací odkaz.",
                    variant: success ? "default" : "destructive",
                  });
                } catch (resendError: any) {
                  toast({
                    title: "Chyba pri odoslaní",
                    description: resendError?.message || "Nepodarilo sa odoslať verifikačný email.",
                    variant: "destructive",
                  });
                }
              }}
            >
              Preposlať email
            </ToastAction>
          ),
        });
        return;
      }
      toast({
        title: "Prihlásenie zlyhalo",
        description: error.message,
        variant: "default",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (credentials: RegisterData) => {
      console.log('[REGISTER] Starting registration mutation with:', credentials);
      const res = await apiRequest("POST", "/api/register", credentials);
      console.log('[REGISTER] Response status:', res.status, res.statusText);
      
      if (!res.ok) {
        const error = await res.json();
        console.log('[REGISTER] Registration error response:', error);
        // Vytvoríme error objekt s code a message
        const errorObj: any = new Error(error.message || 'Registrácia zlyhala');
        errorObj.code = error.code;
        errorObj.email = error.email;
        errorObj.suggestions = error.suggestions;
        throw errorObj;
      }
      
      const result = await res.json();
      console.log('[REGISTER] Registration success:', result);
      return result;
    },
    onSuccess: (user: SelectUser) => {
      console.log('[REGISTER] onSuccess called with user:', user);
      // Presmerujeme na registration success page namiesto toasta
      // Navigácia sa spraví v auth-page.tsx cez state
    },
    onError: (error: Error) => {
      console.log('[REGISTER] onError called with error:', error);
      console.log('[REGISTER] Error message:', error.message);
      // Error handling sa spraví v auth-page.tsx
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/logout");
    },
    onSuccess: () => {
      // Vyčistíme všetky dáta z cache aby sme zabezpečili, že sa nenačítajú žiadne dáta
      // z predchádzajúceho používateľa po prihlásení
      queryClient.clear();
      // Znovu nastavíme základné informácie o používateľovi na null
      queryClient.setQueryData(["/api/user"], null);
    },
    onError: (error: Error) => {
      toast({
        title: "Odhlásenie zlyhalo",
        description: error.message,
        variant: "default",
      });
    },
  });

  // Ak nie je user a nie sme na auth stránke, zobraz fallback
  if (!user && !isLoading && !isAuthRoute) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="p-4 text-sm text-muted-foreground">Redirecting...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
